import * as React from 'react';
import { Field } from '../../StatsbidragGlobals/statsbidrag.types';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import thousands from 'thousands';
import { TextInput } from 'sos-common/js/components/FormControls/TextInput';
import { TextInputForPrint } from 'sos-common/js/components/FormControls/TextInputForPrint';
import { Icon } from 'sos-common/js/components/Icon/icon';
import './_salaryexp.scss';
import { InputError } from 'sos-common/js/components/InputError/InputError';
import {
  calculationComponentProps,
  calculationComponentState,
  calculationComponentModel,
  calculationComponentModel2,
  ColumnTypeGroup,
  Header,
  ColumnValue,
} from '../calculationComponents.types';
import {
  serializeComponent,
  updateField,
  getAsNumber,
  getInitialFieldsFromJsonData,
  valueObject,
  uuidv4,
  getSerializedXmlInputComponent,
  IJsonDataStuctures,
} from '../calculationComponents.helpers';
import EventService from '../calculationComponents.eventservice';
import ValidationService from '../../../services/validation.service';
import { ALL_MANDATORY } from '../calculationComponents.errorMessages';
import { hashCode } from 'sos-common/js/utils/hash';

export interface SalaryExpendituresProps extends calculationComponentProps {
  columnOneHeading: string;
  columnTwoHeading: string;
  columnThreeHeading: string;
  columnFourHeading: string;
  columnFiveHeading: string;
  summaryCellId: string;
  hideAddMore: boolean;
  addMoreText: string;
  columnOnePlaceholder: string;
  columnTwoPlaceholder: string;
  columnThreePlaceholder: string;
  columnFourPlaceholder: string;
  columnThreeExtraText: string;
  sumHeading: string;
  budgetSalarySpecificationCellId: string;
}
export interface SalaryExpendituresState extends calculationComponentState {
  fields: Field[][];
  sum: number;
  budgetSalarySpecificationValue: number;
  errorMessage: string;
  jsonXmlInput: string; //NOTE: can be moved to calculationComponentState once support exists in all kalkylkomponenter.
}

export class SalaryExpenditures extends React.Component<SalaryExpendituresProps, SalaryExpendituresState> {
  private _fieldCount: number;
  private _budgetSalaryChangedCount: number;
  private _errorId: string;

  constructor(props: SalaryExpendituresProps) {
    super(props);
    this._fieldCount = 0;
    this._budgetSalaryChangedCount = 0;

    let initialFields: Field[][] = [this.getFieldRow()];
    this._errorId = hashCode(props.summaryCellId || props.budgetSalarySpecificationCellId, 'error');
    let isValid = true;

    if (props.formElementJson) {
      let calculationComponentModel = JSON.parse(props.formElementJson) as calculationComponentModel;
      initialFields = getInitialFieldsFromJsonData(calculationComponentModel, initialFields, this.getFieldRow);

      let validation = ValidationService.validateComponent(initialFields, props.formElementName);
      isValid = validation.componentIsValid;
      initialFields = validation.fieldsClone;
    }

    this.state = {
      fields: initialFields,
      sum: 0,
      budgetSalarySpecificationValue: 0,
      isValid,
      jsonFields: '',
      isTouched: false,
      errorMessage: '',
      jsonXmlInput: '',
    };
  }

  getSerializedComponent = (fields: Field[][]) => {
    const {
      columnOneHeading,
      columnTwoHeading,
      columnThreeHeading,
      columnFourHeading,
      columnFiveHeading,
      formElementHeading,
      sumHeading,
    } = this.props;
    let values = [];
    fields.forEach((field) => {
      values.push([
        valueObject(field[0].value, true),
        valueObject(thousands(field[1].value, ' '), true),
        valueObject(thousands(field[2].value, ' '), true),
        valueObject(thousands(field[3].value, ' '), true),
        valueObject(thousands(field[4].value, ' '), true),
      ]);
    });
    if (values.length > 0) {
      return serializeComponent(
        [columnOneHeading, columnTwoHeading, columnThreeHeading, columnFourHeading, columnFiveHeading],
        values,
        [sumHeading, thousands(this.state.sum, ' ')],
        formElementHeading
      );
    }
    return '';
  };

  componentDidMount = () => {
    EventService.subscribe('form-is-valid', (e: CustomEvent) => {
      this.setState({
        isTouched: true,
      });
    });
    EventService.subscribe(`cell-updated:${this.props.budgetSalarySpecificationCellId}`, (e: CustomEvent) => {
      this.setState(
        {
          budgetSalarySpecificationValue: getAsNumber(e.detail),
          isTouched: this._budgetSalaryChangedCount > 0,
        },
        () => {
          this._budgetSalaryChangedCount++;
          this.summarizeFields();
        }
      );
    });
  };

  getFieldRow = () => {
    const {
      columnOneHeading,
      columnTwoHeading,
      columnThreeHeading,
      columnFourHeading,
      columnFiveHeading,
      columnOnePlaceholder,
      columnTwoPlaceholder,
      columnThreePlaceholder,
      columnFourPlaceholder,
      formElementName,
    } = this.props;
    return [
      {
        id: `salaryexpenditurefield__${this._fieldCount++}${formElementName}`,
        name: `salaryexpenditurefield__${this._fieldCount}${formElementName}`,
        value: '',
        placeholder: columnOnePlaceholder,
        isValid: true,
        mandatory: true,
        label: columnOneHeading,
        type: 'text',
        isTouched: false,
        errorMessage: 'Du måste ange ' + (columnOneHeading ? columnOneHeading.toLowerCase() : 'denna uppgift'),
      },
      {
        id: `salaryexpenditurefield__${this._fieldCount++}${formElementName}`,
        name: `salaryexpenditurefield__${this._fieldCount}${formElementName}`,
        value: '0',
        placeholder: columnTwoPlaceholder,
        isValid: true,
        mandatory: true,
        label: columnTwoHeading,
        type: 'text',
        actAsNumber: true,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (columnTwoHeading ? columnTwoHeading.toLowerCase() : 'denna uppgift'),
      },
      {
        id: `salaryexpenditurefield__${this._fieldCount++}${formElementName}`,
        name: `salaryexpenditurefield__${this._fieldCount}${formElementName}`,
        value: '0',
        placeholder: columnThreePlaceholder,
        isValid: true,
        mandatory: true,
        label: columnThreeHeading,
        type: 'text',
        actAsNumber: true,
        additionalText: this.props.columnThreeExtraText,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (columnThreeHeading ? columnThreeHeading.toLowerCase() : 'denna uppgift'),
      },
      {
        id: `salaryexpenditurefield__${this._fieldCount++}${formElementName}`,
        name: `salaryexpenditurefield__${this._fieldCount}${formElementName}`,
        value: '0',
        placeholder: columnFourPlaceholder,
        isValid: true,
        mandatory: true,
        label: columnFourHeading,
        type: 'text',
        actAsNumber: true,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (columnFourHeading ? columnFourHeading.toLowerCase() : 'denna uppgift'),
      },
      {
        id: `salaryexpenditurefield__${this._fieldCount++}${formElementName}`,
        name: `salaryexpenditurefield__${this._fieldCount}${formElementName}`,
        value: 0,
        placeholder: '',
        isValid: true,
        mandatory: false,
        label: columnFiveHeading,
        type: 'number',
        isReadOnly: true,
      },
    ] as Field[];
  };

  validateMe = () => {
    let isValid = true;
    let errorMessage = '';

    // Validate standard field logic
    let validation = ValidationService.validateComponent(this.state.fields, this.props.formElementName);
    isValid = validation.componentIsValid;
    let fields = validation.fieldsClone;
    //if (!isValid) errorMessage = ALL_MANDATORY;

    // Validate business logic
    let sumSameAsBudget = this.state.sum === this.state.budgetSalarySpecificationValue;
    if (!sumSameAsBudget) {
      isValid = false;
      errorMessage = `Den totala summan måste vara samma som beloppet för lönekostnader under Budget (${thousands(
        this.state.budgetSalarySpecificationValue,
        ' '
      )})`;
    }

    this.setState(
      {
        fields,
        isValid,
        errorMessage,
      },
      () => {
        ValidationService.validateStepViaElementName(this.props.formElementName);
      }
    );
  };

  summarizeFields = () => {
    const { formElementName, formElementHeading } = this.props;
    let sum = 0;
    this.state.fields.forEach((fieldArr) => {
      sum += getAsNumber(fieldArr[4].value);
    });

    this.setState(
      {
        sum,
      },
      () => {
        this.validateMe();
        EventService.fire(`cell-updated:${this.props.summaryCellId}`, { detail: thousands(this.state.sum, ' ') });
        this.setState({
          jsonFields: this.getSerializedComponent(this.state.fields),
          jsonXmlInput: getSerializedXmlInputComponent(
            this.state.fields,
            calculationComponentModel2.Create(formElementName, formElementHeading, 'Specificerade lönekostnader'),
            this.jsonSeriliazetoMainContentForXmlInput
          ),
        });
      }
    );
  };

  jsonSeriliazetoMainContentForXmlInput = (fields: Field[][], data: IJsonDataStuctures) => {
    if (fields.length == 0) throw 'For this component SalyExpenditures, there must be at least 1 field';
    if (fields[0].length != 5) 'For this component SalyExpenditures, there must be exactly 5 inner fields';

    //adding headers:
    for (let ix = 0; ix < 5; ix++) {
      const id = ix + 1;
      const header = { id, value: fields[0][ix].label } as Header;
      data.headerDefinitions.push(header);
    }

    //adding values:
    fields.forEach((field) => {
      const columnValues = [] as ColumnValue[];
      const staticHeaderColumns = { columnValues } as ColumnTypeGroup;
      data.columnTypeGroups.push(staticHeaderColumns);
      columnValues.push({ headerId: 1, value: field[0].value });
      columnValues.push({ headerId: 2, value: field[1].value });
      columnValues.push({ headerId: 3, value: thousands(field[2].value, ' ') });
      columnValues.push({ headerId: 4, value: field[3].value });
      columnValues.push({ headerId: 5, value: thousands(field[4].value, ' '), isSum: true });
    });

    //adding sum:
    const sumGroup = { isSum: true, columnValues: [] } as ColumnTypeGroup;
    sumGroup.columnValues.push({ headerValue: this.props.sumHeading, value: thousands(this.state.sum, ' ') });
    data.columnTypeGroups.push(sumGroup);
  };

  jsonSerializetoSummaForXmlInput = (): ColumnTypeGroup => {
    const sumColumnType = { isSum: true, columnValues: [] } as ColumnTypeGroup;
    sumColumnType.columnValues.push({ headerValue: this.props.sumHeading, value: thousands(this.state.sum, ' ') });
    return sumColumnType;
  };

  summarizeFieldGroup = (updatedField: Field) => {
    const fieldsClone = this.state.fields.slice();
    const groupToUpdate = fieldsClone.find((x) => x.find((y) => y.name === updatedField.name) !== undefined);
    let groupSum = 0;
    let serviceGrade = getAsNumber(groupToUpdate[1].value);
    let salary = getAsNumber(groupToUpdate[2].value);
    let months = getAsNumber(groupToUpdate[3].value);
    groupSum = salary * months * (serviceGrade / 100);
    groupSum = Math.round(groupSum);

    groupToUpdate[4].value = groupSum;

    this.setState(
      {
        fields: fieldsClone,
      },
      () => {
        this.summarizeFields();
      }
    );
  };

  setValue = (field: FormField<any>) => {
    const fieldsClone = this.state.fields.slice();
    const fieldToUpdate = updateField(field, fieldsClone);

    this.setState(
      {
        fields: fieldsClone,
        //isTouched: true
      },
      () => {
        this.summarizeFieldGroup(fieldToUpdate);
      }
    );
  };

  renderField = (field: Field, index: number) => {
    return (
      !field.isReadOnly && (
        <div className="form-group-small" key={`salaryexpfield_${field.id}`}>
          <label htmlFor={field.id}>{field.label}</label>
          <TextInputForPrint
            id={field.id}
            classNames={this.shouldShowError(field) ? 'has-error' : ''}
            placeholder={field.placeholder}
            value={field.value}
            onChange={this.setValue}
            fieldName={field.name}
            type={field.type}
            additionalText={field.additionalText}
            actAsNumber={field.actAsNumber}
            isRequired={true}
            onBlur={this.onBlur}
            errorAriaAttributes={field.errorAriaAttributes}
          />
          {this.renderError(field)}
        </div>
      )
    );
  };

  renderRows = (row: Field[], index: number) => {
    this.setAriaErrorObjects(row);
    return (
      <div key={`salaryexpgroup_${index}`} className="salaryexp__group form-group-small">
        {row.map(this.renderField)}

        <div className="autosum__summary">
          <div className="autosum__col1">
            <span className="like-a-label">{this.props.columnFiveHeading}</span>
          </div>
          <div className="autosum__col2">
            <input
              className="like-a-label autosum__sumfield"
              id={row[4].id}
              readOnly={true}
              type="text"
              value={thousands(row[4].value, ' ')}
            />
          </div>
        </div>
        {this.state.fields.length > 1 && (
          <div className="delete m-t-2">
            <button type="button" onClick={(e) => this.onRemoveGroup(e, index)} className="stripped-button">
              Ta bort <Icon iconType="Paperbin" />
            </button>
          </div>
        )}
      </div>
    );
  };

  renderError = (field: Field) => {
    if (this.shouldShowError(field) && field.errorAriaAttributes) {
      return (
        <InputError
          key={`${field.id}_cError`}
          id={field.errorAriaAttributes['aria-describedby']}
          errorText={field.errorMessage}
        />
      );
    }
  };

  setAriaErrorObjects = (fieldRow: Field[]) => {
    fieldRow.forEach((field) => {
      field.errorAriaAttributes = null;
      if (this.shouldShowError(field)) {
        field.errorAriaAttributes = { 'aria-invalid': true, 'aria-describedby': uuidv4() };
      }
    });
  };

  shouldShowError = (field: Field) => {
    return (this.state.isTouched || field.isTouched) && !field.isValid;
  };

  onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { fields } = this.state;
    const clone = fields.slice();
    let targetField: Field = null;
    clone.forEach((field) => {
      let foundField = field.find((f) => f.id === e.target.id);
      if (foundField) targetField = foundField;
    });
    if (targetField) {
      targetField.isTouched = true;
      this.setState({ fields: clone });
    }
  };

  onRemoveGroup = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    const fieldsClone = this.state.fields.slice();
    fieldsClone.splice(index, 1);
    this.setState(
      {
        fields: fieldsClone,
        isTouched: true,
      },
      () => {
        this.summarizeFields();
      }
    );
  };

  onAddMore = (e: React.MouseEvent) => {
    e.preventDefault();
    const fieldsClone = this.state.fields.slice();
    const addedRow = this.getFieldRow();
    fieldsClone.push(addedRow);
    this.setState(
      {
        fields: fieldsClone,
        isTouched: true,
      },
      () => {
        const fieldToFocus = document.getElementById(addedRow[0].id);
        if (fieldToFocus) fieldToFocus.focus();
        this.summarizeFields();
      }
    );
  };

  public render() {
    const { summaryCellId, hideAddMore, addMoreText, sumHeading } = this.props;
    const { fields, sum, isValid } = this.state;
    return (
      <div className="salaryexp cc-component">
        {fields && fields.map(this.renderRows)}

        {!hideAddMore && (
          <div className="autosum__btnwrap">
            <button type="button" className="autosum__add" onClick={this.onAddMore}>
              {addMoreText} <Icon iconType="baseline-add_circle_outline-24px" />
            </button>
          </div>
        )}

        <div className="autosum__summary">
          <div className="autosum__col1">
            <span className="like-a-label">{sumHeading}</span>
          </div>
          <div className="autosum__col2">
            <input
              className="like-a-label autosum__sumfield"
              id={summaryCellId}
              readOnly={true}
              type="text"
              value={thousands(sum, ' ')}
            />
          </div>
        </div>
        {this.state.errorMessage && <InputError id={this._errorId} errorText={this.state.errorMessage} />}
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={this.props.formElementName}
          id={this.props.formElementName}
          value={this.state.jsonFields}
        />
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={'__XmlInput' + this.props.formElementName}
          id={'__XmlInput' + this.props.formElementName}
          value={this.state.jsonXmlInput}
        />
      </div>
    );
  }
}
